import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChecklistIcon from "@mui/icons-material/Checklist";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import BusinessIcon from "@mui/icons-material/Business";
import ConstructionIcon from "@mui/icons-material/Construction";
import GavelIcon from "@mui/icons-material/Gavel";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentsIcon from "@mui/icons-material/Payments";
import MailIcon from "@mui/icons-material/Mail";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TopNav = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.resolvedLanguage;
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("profile")),
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isRouteActive = (path) =>
    location.pathname.toLowerCase().includes(path.toLowerCase());

  const isApp = location.pathname.toLowerCase().includes("/app/");
  const isRemovedAppLanding = /\/app\/(main|accounting)\/?$/i.test(
    location.pathname,
  );

  const isModernPOSTerminal = location.pathname
    .toLowerCase()
    .includes("/app/pos/terminal/");

  if (!user || !isApp || isRemovedAppLanding || isModernPOSTerminal) return null;

  const menuGroups = [
    {
      key: "modules",
      label: t("Business Modules"),
      icon: <BusinessIcon fontSize="small" />,
      items: [
        {
          label: t("construction"),
          to: `/${currentLang}/app/construction/projects`,
          icon: <ConstructionIcon fontSize="small" />,
          active: isRouteActive("/app/construction"),
        },
        {
          label: t("law_firm"),
          to: `/${currentLang}/app/law-firm`,
          icon: <GavelIcon fontSize="small" />,
          active: isRouteActive("/app/law-firm"),
        },
        {
          label: t("clinic"),
          to: `/${currentLang}/app/clinic`,
          icon: <MedicalServicesIcon fontSize="small" />,
          active: isRouteActive("/app/clinic"),
        },
        {
          label: t("rentals"),
          to: `/${currentLang}/app/rentals/home`,
          icon: <ApartmentIcon fontSize="small" />,
          active: isRouteActive("/app/rentals"),
        },
        {
          label: t("properties"),
          to: `/${currentLang}/app/properties`,
          icon: <HomeWorkIcon fontSize="small" />,
          active: isRouteActive("/app/properties"),
        },
      ],
    },
    {
      key: "management",
      label: t("Management"),
      icon: <WorkIcon fontSize="small" />,
      items: [
        {
          label: t("attendance"),
          to: `/${currentLang}/app/attendance/home`,
          icon: <AccessTimeIcon fontSize="small" />,
          active: isRouteActive("/app/attendance"),
        },
        {
          label: t("payrolls"),
          to: `/${currentLang}/app/payrolls`,
          icon: <PaymentsIcon fontSize="small" />,
          active: isRouteActive("/app/payrolls"),
        },
        {
          label: t("letters"),
          to: `/${currentLang}/app/letters/home`,
          icon: <MailIcon fontSize="small" />,
          active: isRouteActive("/app/letters"),
        },
        {
          label: t("import/export"),
          to: `/${currentLang}/app/excel`,
          icon: <ImportExportIcon fontSize="small" />,
          active: isRouteActive("/app/excel"),
        },
      ],
    },
    {
      key: "system",
      label: t("System"),
      icon: <SettingsIcon fontSize="small" />,
      items: [
        {
          label: t("admin_settings"),
          to: `/${currentLang}/app/admin-settings`,
          icon: <AdminPanelSettingsIcon fontSize="small" />,
          active: isRouteActive("/app/admin-settings"),
        },
        {
          label: t("settings"),
          to: `/${currentLang}/app/settings`,
          icon: <SettingsIcon fontSize="small" />,
          active:
            isRouteActive("/app/settings") &&
            !isRouteActive("/app/admin-settings"),
        },
        {
          label: t("my_account"),
          to: `/${currentLang}/my-account`,
          icon: <SubscriptionsIcon fontSize="small" />,
          active:
            isRouteActive("/my-account") ||
            isRouteActive("/my-subscriptions") ||
            isRouteActive("/domains-and-subdomains") ||
            isRouteActive("/my-invoices") ||
            isRouteActive("/my-orders") ||
            isRouteActive("/my-tasks") ||
            isRouteActive("/my-documents") ||
            isRouteActive("/my-knowledge") ||
            isRouteActive("/backup-and-restore") ||
            isRouteActive("/app/my-subscription"),
        },
        {
          label: t("my_billing"),
          to: `/${currentLang}/my-billing`,
          icon: <ReceiptIcon fontSize="small" />,
          active: isRouteActive("/my-billing"),
        },
      ],
    },
  ];

  return (
    <nav ref={navRef} className="top-nav np">
      {/* Desktop navigation */}
      <div className="top-nav-inner">
        {/* Logo */}
        <Link to={`/${currentLang}/my-account`} className="top-nav-logo">
          <img src="/logo.svg" alt="logo" style={{ height: "28px" }} />
        </Link>

        {/* Direct links */}
        <div className="top-nav-links">
          <Link
            to={`/${currentLang}/app/customers`}
            className={`tnl ${isRouteActive("/app/customers") ? "tnl-active" : ""}`}
          >
            <PeopleIcon fontSize="small" />
            <span>{t("customers")}</span>
          </Link>

          <Link
            to={`/${currentLang}/app/inventory/home`}
            className={`tnl ${isRouteActive("/app/inventory") ? "tnl-active" : ""}`}
          >
            <InventoryIcon fontSize="small" />
            <span>{t("inventory")}</span>
          </Link>

          {/* Dropdown menu groups (excluding System) */}
          {menuGroups
            .filter((g) => g.key !== "system")
            .map((group) => {
              const isGroupActive = group.items.some((item) => item.active);
              return (
                <div key={group.key} className="tnl-dropdown">
                  <button
                    className={`tnl tnl-btn ${isGroupActive ? "tnl-active" : ""}`}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === group.key ? null : group.key,
                      )
                    }
                  >
                    {group.icon}
                    <span>{group.label}</span>
                    <ExpandMoreIcon
                      fontSize="small"
                      className={`tnl-expand ${
                        openDropdown === group.key ? "tnl-expand-open" : ""
                      }`}
                    />
                  </button>

                  {openDropdown === group.key && (
                    <div className="tnl-dropdown-menu">
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`tnl-dropdown-item ${
                            item.active ? "tnl-dropdown-item-active" : ""
                          }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

          {/* To-Do List — before Dashboard */}
          <Link
            to={`/${currentLang}/app/todos`}
            className={`tnl ${isRouteActive("/app/todos") ? "tnl-active" : ""}`}
          >
            <ChecklistIcon fontSize="small" />
            <span>{t("todo_lists")}</span>
          </Link>

          {/* Dashboard — before System */}
          <Link
            to={`/${currentLang}/app/dashboard`}
            className={`tnl ${isRouteActive("/app/dashboard") ? "tnl-active" : ""}`}
          >
            <DashboardIcon fontSize="small" />
            <span>{t("dashboard")}</span>
          </Link>

          {/* System dropdown */}
          {menuGroups
            .filter((g) => g.key === "system")
            .map((group) => {
              const isGroupActive = group.items.some((item) => item.active);
              return (
                <div key={group.key} className="tnl-dropdown">
                  <button
                    className={`tnl tnl-btn ${isGroupActive ? "tnl-active" : ""}`}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === group.key ? null : group.key,
                      )
                    }
                  >
                    {group.icon}
                    <span>{group.label}</span>
                    <ExpandMoreIcon
                      fontSize="small"
                      className={`tnl-expand ${openDropdown === group.key ? "tnl-expand-open" : ""}`}
                    />
                  </button>
                  {openDropdown === group.key && (
                    <div className="tnl-dropdown-menu">
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`tnl-dropdown-item ${item.active ? "tnl-dropdown-item-active" : ""}`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Mobile hamburger button */}
      <button
        className="top-nav-mobile-btn"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="toggle mobile menu"
      >
        {mobileOpen ? <CloseIcon /> : <MenuIcon />}
      </button>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="top-nav-mobile-menu">
          <Link
            to={`/${currentLang}/app/customers`}
            className={`mnl ${isRouteActive("/app/customers") ? "mnl-active" : ""}`}
          >
            <PeopleIcon fontSize="small" />
            <span>{t("customers")}</span>
          </Link>

          <Link
            to={`/${currentLang}/app/inventory/home`}
            className={`mnl ${isRouteActive("/app/inventory") ? "mnl-active" : ""}`}
          >
            <InventoryIcon fontSize="small" />
            <span>{t("inventory")}</span>
          </Link>

          {menuGroups
            .filter((g) => g.key !== "system")
            .map((group) => (
              <div key={group.key} className="mnl-group">
                <button
                  className="mnl-group-header"
                  onClick={() =>
                    setMobileExpanded(
                      mobileExpanded === group.key ? null : group.key,
                    )
                  }
                >
                  <span className="mnl-group-label">
                    {group.icon}
                    {group.label}
                  </span>
                  <ExpandMoreIcon
                    fontSize="small"
                    className={`tnl-expand ${
                      mobileExpanded === group.key ? "tnl-expand-open" : ""
                    }`}
                  />
                </button>
                {mobileExpanded === group.key &&
                  group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`mnl mnl-sub ${item.active ? "mnl-active" : ""}`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
              </div>
            ))}

          {/* To-Do List — before Dashboard */}
          <Link
            to={`/${currentLang}/app/todos`}
            className={`mnl ${isRouteActive("/app/todos") ? "mnl-active" : ""}`}
          >
            <ChecklistIcon fontSize="small" />
            <span>{t("todo_lists")}</span>
          </Link>

          {/* Dashboard — before System */}
          <Link
            to={`/${currentLang}/app/dashboard`}
            className={`mnl ${isRouteActive("/app/dashboard") ? "mnl-active" : ""}`}
          >
            <DashboardIcon fontSize="small" />
            <span>{t("dashboard")}</span>
          </Link>

          {/* System group */}
          {menuGroups
            .filter((g) => g.key === "system")
            .map((group) => (
              <div key={group.key} className="mnl-group">
                <button
                  className="mnl-group-header"
                  onClick={() =>
                    setMobileExpanded(
                      mobileExpanded === group.key ? null : group.key,
                    )
                  }
                >
                  <span className="mnl-group-label">
                    {group.icon}
                    {group.label}
                  </span>
                  <ExpandMoreIcon
                    fontSize="small"
                    className={`tnl-expand ${mobileExpanded === group.key ? "tnl-expand-open" : ""}`}
                  />
                </button>
                {mobileExpanded === group.key &&
                  group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`mnl mnl-sub ${item.active ? "mnl-active" : ""}`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
              </div>
            ))}
        </div>
      )}
    </nav>
  );
};

export default TopNav;
